'use client';

import { tryit } from 'radash';
import { useState } from 'react';

import { makeFileSchema, type UploadFileResultType } from './types';

/**
 * Per-call overrides for upload validation and transfer. Omitted, validation matches the shared
 * default (10MB, standard document/image MIME set) and the PUT sends only `Content-Type: file.type`.
 * `headers` are the exact headers signed into the presigned URL, and the PUT sends only these.
 */
type UploadOptions = { maxSize?: number; anyType?: boolean; headers?: Record<string, string> };

/**
 * Uploads a file to the S3 presigned URL
 */
async function putFile(url: string, body: Blob, headers: Record<string, string>) {
  return fetch(url, { method: 'PUT', body, headers }).then(res => {
    if (res.ok) {
      return res.text();
    }
    throw new Error('Failed to upload');
  });
}

export function useUploadFile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (presignedURL: UploadFileResultType, file: File, options?: UploadOptions) => {
    const validationResult = makeFileSchema(options).safeParse({ type: file.type, size: file.size });
    if (!validationResult.success) {
      setError(validationResult.error.errors[0].message);
      return {
        error: validationResult.error.errors[0].message,
      };
    }

    setLoading(true);

    // fetch sends a Blob body's type as Content-Type when no header sets it, so signed headers go
    // with a typeless copy of the file.
    const [err] = options?.headers
      ? await tryit(putFile)(presignedURL.putFileUrl, file.slice(), options.headers)
      : await tryit(putFile)(presignedURL.putFileUrl, file, { 'Content-Type': file.type });
    if (err) {
      setLoading(false);
      setError('Failed to upload');
      return {
        error: 'Failed to upload',
      };
    }
    setLoading(false);

    return {
      url: presignedURL.url,
      path: presignedURL.path,
    };
  };

  return {
    upload,
    loading,
    error,
  };
}

export * from './types';
